// 新建方案页（任务 1.3 / 1.7）· 引导式表单 → 创建项目 → 启动生成任务
// 视觉统一策略咖品牌语言（与 Projects 页同款）；
// 创建时可选视觉主题（041 T3.2：10 套主题卡按风格分 3 组，默认 swiss-ikb）与「生成配图」开关，
// 生成完成后仍可在编辑器右栏「🎨 视觉主题」整册即时换肤。
// 1026 F1：应用场景可选（新品上市/促销活动/产品推广/事件营销/品牌公关，随 form.scenario 进入 intake 事实库）
// 1026 F3：文档资料上传入口（pdf/docx/txt/md/csv 直传 inputs 桶，worker intake 解析为事实来源）
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import { DEFAULT_THEME_ID, THEMES, getTheme } from "../lib/tokens.generated";
import { STYLE_GROUPS, chipColors } from "../lib/theme-ui";
import { newSubmission, submitProject, type SubmissionIntent } from "../lib/project-submission";
import { BrandLogo } from "../components/BrandLogo";
import "./NewProject.css";

const INDUSTRIES = ["消费品/零售", "餐饮/连锁", "教育/培训", "美妆/个护", "母婴", "科技/SaaS", "其他"];

// 1026 F1：方案应用场景（单选，可再点取消 = 不指定）
const SCENARIOS = ["新品上市", "促销活动", "产品推广", "事件营销", "品牌公关"];

// 1026 F3：与 _shared/docparse 支持的解析类型保持一致
const UPLOAD_ACCEPT = ".pdf,.docx,.txt,.md,.csv";
const UPLOAD_LIMIT = 5;
const UPLOAD_MAX_MB = 20;

interface Draft {
  name?: string; industry?: string; product?: string; audience?: string; goal?: string; budget?: string;
  scenario?: string; themeId?: string; artwork?: boolean; intent?: SubmissionIntent;
}
function readDraft(key: string): Draft {
  try { return JSON.parse(sessionStorage.getItem(key) || "{}") || {}; } catch { return {}; }
}
function writeDraft(key: string, draft: Draft) {
  try { sessionStorage.setItem(key, JSON.stringify(draft)); } catch { /* Private browsing/storage quota must not block submission. */ }
}

export default function NewProject() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const draftKey = `marketing:new-project:${user?.id}`;
  const [draft] = useState(() => readDraft(draftKey));
  const [name, setName] = useState(draft.name || "");
  const [industry, setIndustry] = useState(draft.industry || INDUSTRIES[0]);
  const [product, setProduct] = useState(draft.product || "");
  const [audience, setAudience] = useState(draft.audience || "");
  const [goal, setGoal] = useState(draft.goal || "");
  const [budget, setBudget] = useState(draft.budget || "");
  const [scenario, setScenario] = useState(draft.scenario || "");
  const [files, setFiles] = useState<File[]>([]);
  const [themeId, setThemeId] = useState<string>(draft.themeId && THEMES[draft.themeId] ? draft.themeId : DEFAULT_THEME_ID);
  // 048 F1：主题选择默认折叠（输入阶段不干扰），生成完成后在编辑器对比切换才是主路径
  const [showThemes, setShowThemes] = useState(false);
  const [artwork, setArtwork] = useState(draft.artwork ?? true); // 默认开启
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [phase, setPhase] = useState("");
  const [intent, setIntent] = useState<SubmissionIntent | undefined>(draft.intent);
  const inFlight = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const currentDraft: Draft = { name, industry, product, audience, goal, budget, scenario, themeId, artwork, intent };
  useEffect(() => {
    writeDraft(draftKey, { name, industry, product, audience, goal, budget, scenario, themeId, artwork, intent });
  }, [draftKey, name, industry, product, audience, goal, budget, scenario, themeId, artwork, intent]);

  function pickFiles(list: FileList | null) {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) {
      if (next.length >= UPLOAD_LIMIT) break;
      if (f.size > UPLOAD_MAX_MB * 1024 * 1024) { setErr(`「${f.name}」超过 ${UPLOAD_MAX_MB}MB，请先压缩或拆分。`); continue; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
    }
    setFiles(next);
    if (fileInput.current) fileInput.current.value = "";
  }

  /** 1026 F3：项目创建后、任务启动前，把用户文档直传 inputs 桶（assets kind=input 行由 API 端点登记） */
  async function uploadMaterials(projectId: string) {
    for (const f of files) {
      const up = await api.createInputUpload(projectId, f.name);
      const { error } = await supabase.storage.from("inputs").uploadToSignedUrl(up.path, up.token, f);
      if (error) throw new Error(`「${f.name}」上传失败：${error.message}`);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setErr(null);
    const submission = intent || newSubmission({
      name: name || "未命名项目", industry,
      form: { product, audience, goal, budget, scenario, style: getTheme(themeId).style, theme_id: themeId, artwork },
    });
    const save = (next: SubmissionIntent) => {
      setIntent(next);
      writeDraft(draftKey, { ...currentDraft, intent: next });
    };
    save(submission);
    try {
      const jobId = await submitProject(submission, api, save, setPhase, {
        beforeStartJob: files.length ? uploadMaterials : undefined,
      });
      try { sessionStorage.removeItem(draftKey); } catch { /* Storage is optional. */ }
      nav(`/job/${jobId}`);
    } catch (e) {
      setErr(`${(e as Error).message} 资料已保留，可点击「继续本次提交」。`);
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  }

  return (
    <div className="aim-new">
      <header className="aim-new-header">
        <Link className="aim-new-wordmark" to="/projects" aria-label="策略咖 · 我的方案">
          <BrandLogo />
        </Link>
        <nav>
          <Link to="/projects">我的方案</Link>
          <span>{user?.email}</span>
          <button onClick={() => signOut()}>退出 <i aria-hidden="true">↗</i></button>
        </nav>
      </header>

      <main>
        <section className="aim-new-head">
          <p className="aim-new-eyebrow">NEW PROJECT / BRIEF</p>
          <h1>把品牌资料交给我，<br /><em>提案交给 AI。</em></h1>
          <p className="aim-new-lede">填写以下信息，AI 流水线将自动调研、策略、撰写，产出 10 页 PPT。</p>
          <div className="aim-new-head-signal" aria-hidden="true"><i /><i /></div>
        </section>

        <section className="aim-new-body">
          <div className="aim-new-section-title">
            <p className="aim-new-eyebrow">PROJECT BRIEF / 品牌资料</p>
            <span>BRIEF · VISUAL</span>
          </div>
          <form className="aim-new-form" onSubmit={submit}>
            <fieldset className="aim-new-fields" disabled={busy || Boolean(intent)}>
            <div className="aim-new-field">
              <label htmlFor="np-name">项目 / 品牌名称</label>
              <input id="np-name" className="aim-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="如：海龟爸爸" />
            </div>
            <div className="aim-new-field">
              <label htmlFor="np-industry">所属行业</label>
              <select id="np-industry" className="aim-input" value={industry} onChange={(e) => setIndustry(e.target.value)}>
                {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
              </select>
            </div>
            {/* 1026 F1：应用场景可选（影响 intake 事实库与后续策略侧重） */}
            <div className="aim-new-field is-full">
              <label>应用场景 <span>（可选：选择一个最贴近本次任务的营销场景）</span></label>
              <div className="aim-new-chips" role="group" aria-label="应用场景">
                {SCENARIOS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`aim-new-chip ${scenario === s ? "is-active" : ""}`}
                    aria-pressed={scenario === s}
                    onClick={() => setScenario((cur) => (cur === s ? "" : s))}
                  >{s}</button>
                ))}
              </div>
            </div>
            <div className="aim-new-field is-full">
              <label htmlFor="np-product">核心产品 / 服务</label>
              <textarea id="np-product" className="aim-input" rows={2} value={product} onChange={(e) => setProduct(e.target.value)} placeholder="一句话描述您卖什么、有何不同" />
            </div>
            <div className="aim-new-field">
              <label htmlFor="np-audience">目标人群</label>
              <input id="np-audience" className="aim-input" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="如：3-12 岁儿童的家长" />
            </div>
            <div className="aim-new-field">
              <label htmlFor="np-goal">营销目标</label>
              <input id="np-goal" className="aim-input" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="如：新品上市 3 个月破圈 + 转化" />
            </div>
            <div className="aim-new-field is-full">
              <label htmlFor="np-budget">预算量级 <span>（可选）</span></label>
              <input id="np-budget" className="aim-input" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="如：50 万 / 季度" />
            </div>

            {/* 1026 F3：文档资料上传入口——pdf/docx/txt/md/csv，生成时作为事实来源解析 */}
            <div className="aim-new-field is-full">
              <label>上传文档资料 <span>（可选：产品手册、品牌介绍等，最多 {UPLOAD_LIMIT} 份，单份 ≤{UPLOAD_MAX_MB}MB，支持 PDF / Word / TXT / MD / CSV）</span></label>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={UPLOAD_ACCEPT}
                onChange={(e) => pickFiles(e.target.files)}
              />
              {files.length > 0 && (
                <ul className="aim-new-files">
                  {files.map((f) => (
                    <li key={`${f.name}-${f.size}`}>
                      <span>{f.name}<small>（{(f.size / 1024 / 1024).toFixed(1)}MB）</small></span>
                      <button type="button" onClick={() => setFiles(files.filter((x) => x !== f))} aria-label={`移除 ${f.name}`}>移除</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 视觉主题（044 T3.2 / 048 F1）：默认折叠的高级选项——先用推荐主题出片，
                完成后在编辑器右栏对比切换；此处只给有明确偏好的用户展开选 */}
            <div className="aim-new-field is-full">
              <label>视觉主题 <span>（可选：默认用推荐主题，生成完成后仍可整册换肤）</span></label>
              {!showThemes ? (
                <button type="button" className="aim-new-theme-toggle" onClick={() => setShowThemes(true)}>
                  当前：{getTheme(themeId).name} · 展开选择全部 {Object.keys(THEMES).length} 套主题 ▾
                </button>
              ) : (
                <div className="space-y-2">
                  <button type="button" className="aim-new-theme-toggle" onClick={() => setShowThemes(false)}>
                    收起主题选择 ▴（不着急定：生成完成后在编辑器里对比切换更直观）
                  </button>
                {STYLE_GROUPS.map((g) => (
                  <div key={g.id}>
                    <div className="mb-1 text-[11px] font-medium text-secondary">{g.label}</div>
                    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                      {Object.values(THEMES).filter((t) => t.style === g.id).map((t) => {
                        const active = themeId === t.id;
                        // 双色 chip 取色见 lib/theme-ui.ts（accent 系 + 底纸系）
                        const [c1, c2] = chipColors(t);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setThemeId(t.id)}
                            title={t.suitable}
                            className={`flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-left text-[11px] transition ${
                              active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <span className="flex h-4 w-4 flex-none overflow-hidden rounded-sm border border-slate-200">
                              <span className="h-full w-1/2" style={{ background: c1 }} />
                              <span className="h-full w-1/2" style={{ background: c2 }} />
                            </span>
                            <span className="truncate">{t.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                </div>
              )}
            </div>

            {/* 生成配图开关（默认开；不勾则后端 artwork 步骤跳过，图文页保留占位框） */}
            <div className="aim-new-field is-full">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={artwork}
                  onChange={(e) => setArtwork(e.target.checked)}
                />
                生成配图（AI 为图文页自动生成配图；关闭可加快出片）
              </label>
            </div>

            </fieldset>
            {intent && !busy && <p className="aim-new-hint">本次提交资料已保留。继续提交会接续原项目；也可到 <Link to="/projects">我的方案</Link> 查看。</p>}
            {busy && <p className="aim-new-hint" role="status" aria-live="polite">{phase} 网络较慢时会在 30 秒内提示结果。</p>}
            {err && <p className="aim-new-error" role="alert">{err}</p>}
            <button className="aim-new-submit" disabled={busy}>
              {busy ? phase || "正在连接…" : intent ? "继续本次提交" : "开始生成方案"}<b aria-hidden="true">↗</b>
            </button>
            <p className="aim-new-hint">不必现在纠结主题：生成完成后，在编辑器右栏「🎨 视觉主题」可整册即时换肤，多套对比后再导出。</p>
          </form>
        </section>
      </main>
    </div>
  );
}
